import type { NewCardContext, PipelineStepContext, PipelineStepResult } from '..';
import { PaymentProcessor, PipelineBaseStep, usePaymentMethodStore } from '..';

/**
 * EncryptionStep - Handles card encryption using processor-specific methods
 *
 * This step:
 * 1. Gets the encryption service from context.services
 * 2. Calls the service to encrypt card data (triggers WebView)
 * 3. Returns encrypted data to be used by subsequent steps
 *
 * The encryption service uses a WebView to keep card data secure
 * and never exposes it directly to JavaScript
 */
export class EncryptionStep extends PipelineBaseStep {
  readonly name = 'EncryptionStep';

  async execute(context: PipelineStepContext): Promise<PipelineStepResult> {
    this.logger.log('Encrypting card data', { processor: context.processor });

    // Only run for new card payments
    if (context.paymentMethod !== 'newCard') {
      this.logger.log('Skipping encryption - not a new card payment');
      return this.success();
    }

    const newCardContext = context as NewCardContext;

    const encryptionService = usePaymentMethodStore.getState().encryptionService;

    if (!encryptionService) {
      this.logger.logError('Encryption service not provided in context');
      return this.error(
        'ENCRYPTION_SERVICE_MISSING',
        'Encryption service not provided in payment context',
        'Unable to process payment. Please try again. Error code: (Missing Encryption)'
      );
    }

    try {
      this.logger.log('Starting card encryption', {
        processor: newCardContext.processor,
        orderId: newCardContext.rbiOrderId,
      });

      // Call the encryption service - this will trigger the WebView
      // and return a Promise that resolves when encryption completes
      const encryptionResult = await encryptionService.encrypt(
        newCardContext.cardData,
        newCardContext.processor,
        newCardContext.rbiOrderId
      );

      this.logger.log('Encryption successful', {
        processor: newCardContext.processor,
        resultType:
          newCardContext.processor === PaymentProcessor.ORBITAL
            ? 'OrbitalEncryption'
            : 'WorldpayEncryption',
      });

      // Return success with the encryption result
      // This gets merged into the context for subsequent steps
      return this.success({
        encryptionResult,
      });
    } catch (error) {
      this.logger.logError('Encryption failed', error);

      // Encryption failures can be retried from this step
      return this.error(
        'ENCRYPTION_FAILED',
        error instanceof Error ? error.message : 'Card encryption failed',
        'Unable to process card details. Please check your card information and try again. Error code: (Encryption Error)',
        'EncryptionStep' // retryFromStep - allow retry of encryption
      );
    }
  }
}
